import { useCallback, useEffect } from 'react';

interface UsePrintOptions {
  /** ID(s) of the element(s) to print */
  targetId: string | string[];
  /** Custom filename suggestion for the PDF */
  filename?: string;
  /** Callback fired before printing */
  onBeforePrint?: () => void;
  /** Callback fired after printing */
  onAfterPrint?: () => void;
  /** Custom CSS to inject during print */
  customPrintStyles?: string;
  /** Elements to hide during print (CSS selectors) */
  hideOnPrint?: string[];
}

/**
 * Custom hook to handle printing/PDF export functionality.
 * Uses the browser's native print API - user selects "Save as PDF" in print dialog.
 * 
 * @example
 * ```tsx
 * const { handlePrint } = usePrint({
 *   targetId: 'my-content',
 *   filename: 'report.pdf',
 *   onBeforePrint: () => console.log('Printing...'),
 * });
 * 
 * <button onClick={handlePrint}>Print</button>
 * ```
 */
export const usePrint = ({
  targetId,
  filename,
  onBeforePrint,
  onAfterPrint,
  customPrintStyles,
  hideOnPrint = ['.no-print', 'button', '.MuiIconButton-root'],
}: UsePrintOptions) => {
  // Set up print event listeners
  useEffect(() => {
    const beforePrint = () => {
      onBeforePrint?.();
    };

    const afterPrint = () => {
      onAfterPrint?.();
    };

    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);

    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [onBeforePrint, onAfterPrint]);

  const handlePrint = useCallback(() => {
    // Update document title for PDF filename suggestion
    const originalTitle = document.title;
    if (filename) {
      document.title = filename.replace('.pdf', '');
    }

    // Inject custom print styles if provided
    let styleElement: HTMLStyleElement | null = null;
    if (customPrintStyles) {
      styleElement = document.createElement('style');
      styleElement.id = 'custom-print-styles';
      styleElement.textContent = `@media print { ${customPrintStyles} }`;
      document.head.appendChild(styleElement);
    }

    // Get target element(s)
    const targetIds = Array.isArray(targetId) ? targetId : [targetId];
    const targetElements = targetIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (targetElements.length === 0) {
      console.warn('Print target element(s) not found:', targetId);
      return;
    }

    // Add print-specific classes
    targetElements.forEach((el) => {
      el.classList.add('printing-active');
    });

    // Add class to body to enable print-specific styles
    document.body.classList.add('print-mode');

    // Trigger print dialog
    window.print();

    // Cleanup after print dialog closes
    setTimeout(() => {
      // Restore original title
      document.title = originalTitle;

      // Remove custom styles
      if (styleElement) {
        styleElement.remove();
      }

      // Remove print-specific classes
      targetElements.forEach((el) => {
        el.classList.remove('printing-active');
      });
      document.body.classList.remove('print-mode');
    }, 100);
  }, [targetId, filename, customPrintStyles]);

  return { handlePrint };
};

export default usePrint;

